import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { IPurchasesService } from './IPurchasesService';
import { PurchasesEndpoints } from './purchasesEndpoints';
import {
    toActiveDraft,
    toImportHistoryEntry,
    toPage,
    toPendingInvoice,
    toPurchaseSummary,
    toReceivedInvoice,
} from './purchasesMapping';
import type { PurchaseListQuery } from './queries';
import type { DraftInvoiceRequest, ImportPurchaseRequest } from './requests';
import type {
    GetDraftInvoiceResponse,
    GetDraftInvoicesResponse,
    GetImportHistoryResponse,
    GetIncomingInvoicesResponse,
    GetPurchaseSummaryResponse,
    GetReceivedInvoicesResponse,
    PurchaseInvoiceDetailResponse,
} from './responses';

type PurchasesServiceOptions = {
    baseUrl: string;
};

/**
 * Client for the purchases endpoints. The four lists accept pagination
 * params (`page`/`pageSize`, which bos-api binds case-insensitively to
 * `Page`/`PageSize`); the summary takes none. Draft invoices have
 * full CRUD (bos-api BOS-55): GET/POST/PUT echo the saved nested draft,
 * DELETE soft-deletes (204). The final import (bos-api BOS-57) is one
 * POST handling both flows — fresh invoice, or in-place transition of
 * the draft named by `draftId`.
 */
export class PurchasesService implements IPurchasesService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: PurchasesServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getIncomingInvoices(
        query?: PurchaseListQuery,
    ): Promise<ApiResponse<GetIncomingInvoicesResponse>> {
        const url = PurchasesEndpoints.incomingInvoices();
        const result = await makeApiRequest<GetIncomingInvoicesResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: query,
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toPage(result.response, toPendingInvoice),
              }
            : result;
    }

    public async getDraftInvoices(
        query?: PurchaseListQuery,
    ): Promise<ApiResponse<GetDraftInvoicesResponse>> {
        const url = PurchasesEndpoints.draftInvoices();
        const result = await makeApiRequest<GetDraftInvoicesResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: query,
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toPage(result.response, toActiveDraft),
              }
            : result;
    }

    public getDraftInvoice(
        id: string,
    ): Promise<ApiResponse<GetDraftInvoiceResponse>> {
        const url = PurchasesEndpoints.draftInvoice(id);
        return makeApiRequest<GetDraftInvoiceResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public createDraftInvoice(
        body: DraftInvoiceRequest,
    ): Promise<ApiResponse<GetDraftInvoiceResponse>> {
        // POST returns 201 + the saved shape; the server assigns the id.
        const url = PurchasesEndpoints.draftInvoices();
        return makeApiRequest<GetDraftInvoiceResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public updateDraftInvoice(
        id: string,
        body: DraftInvoiceRequest,
    ): Promise<ApiResponse<GetDraftInvoiceResponse>> {
        // Full replace — the body carries the whole draft every time.
        const url = PurchasesEndpoints.draftInvoice(id);
        return makeApiRequest<GetDraftInvoiceResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
    }

    public deleteDraftInvoice(id: string): Promise<ApiResponse<void>> {
        const url = PurchasesEndpoints.draftInvoice(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }

    public importPurchase(
        body: ImportPurchaseRequest,
    ): Promise<ApiResponse<PurchaseInvoiceDetailResponse>> {
        // POST creates a Received invoice in one round-trip (201); with a
        // draftId the named Draft transitions in place, keeping its id.
        const url = PurchasesEndpoints.importPurchase();
        return makeApiRequest<PurchaseInvoiceDetailResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, body),
        );
    }

    public async getImportHistory(
        query?: PurchaseListQuery,
    ): Promise<ApiResponse<GetImportHistoryResponse>> {
        const url = PurchasesEndpoints.importHistory();
        const result = await makeApiRequest<GetImportHistoryResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: query,
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toPage(result.response, toImportHistoryEntry),
              }
            : result;
    }

    public async getReceivedInvoices(
        query?: PurchaseListQuery,
        supplierId?: string,
    ): Promise<ApiResponse<GetReceivedInvoicesResponse>> {
        const url = PurchasesEndpoints.receivedInvoices();
        const result = await makeApiRequest<GetReceivedInvoicesResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url, {
                params: supplierId ? { ...query, supplierId } : query,
            }),
        );
        return result.success
            ? {
                  success: true,
                  response: toPage(result.response, toReceivedInvoice),
              }
            : result;
    }

    public async getSummary(): Promise<
        ApiResponse<GetPurchaseSummaryResponse>
    > {
        const url = PurchasesEndpoints.summary();
        const result = await makeApiRequest<GetPurchaseSummaryResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
        return result.success
            ? { success: true, response: toPurchaseSummary(result.response) }
            : result;
    }
}
